/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@shopify/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats array of strings from shopify metafields.
 * @param {String} stringData - Single line text entry from shopify metafield.
 * @returns {Array}
 */
function returnArrayOfStrings(stringData) {
  return stringData ? JSON.parse(stringData) : null
}

/**
 * Filters out videos and returns them in an array.
 * @param {Object} videoObj - Shopify media object.
 * @returns {Array}
 */
function returnVideoContentInMedia(videoObj) {

  const videos = videoObj?.edges.reduce((agg, item) => {
    if (item.node.mediaContentType !== 'VIDEO') {
      return agg
    }
    agg.push(item.node)
    return agg
  }, [])
  return videos
}

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.root}/products/${product.handle}`

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    filterColor: product.filterColor?.value ? product.filterColor.value : false,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    options: formatOptions(product),
    price: formatPrice(product.priceRange),
    swatchColor: product.swatchColor?.value ? product.swatchColor.value : false,
    tags: product.tags,
    title: product.title,
    type: product.productType,
    url,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Formats response from product for product page.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatPDPProduct(response) {
  const product = formatProduct(response)

  const sustainabilityBullets = []

  if (product?.sustainability_bullet_one?.value) {
    sustainabilityBullets.push(product?.sustainability_bullet_one?.value)
  }
  if (product?.sustainability_bullet_two?.value) {
    sustainabilityBullets.push(product?.sustainability_bullet_two?.value)
  }
  if (product?.sustainability_bullet_three?.value) {
    sustainabilityBullets.push(product?.sustainability_bullet_three?.value)
  }

  const subMenu = {
    details: {
      lower: returnArrayOfStrings(product?.fit_details_lower?.value),
      modelDescription: product?.fit_model_description?.value ?? null,
      upper: returnArrayOfStrings(product?.fit_details?.value),
    },
    delivery: {},
    sustainability: {
      bullets: sustainabilityBullets,
      links: returnArrayOfStrings(product?.sustainability_links?.value),
      text: product?.sustainability_text?.value,
    },
  }

  const sizeGuide = {
    sizeConversion: product?.fit_size_conversion?.value ?? null,
    bodyMeasurement: product?.fit_body_measurement?.value ?? null,
    sizeDiagram: product?.fit_size_diagram?.reference?.image?.url ?? null,
    sizeDiagramDetail: product?.fit_size_diagram_detail?.reference?.image?.url ?? null,
    keyPoints: returnArrayOfStrings(product?.fit_details_lower?.value),
    modelDescription: product?.fit_model_description?.value ?? null,
    fitTextLong: product?.fit_text_long?.value ?? null,
  }

  const formattedProduct = {
    ...product,
    preOrder: {
      text: product.pre_order_text?.value || null,
      bullets: returnArrayOfStrings(product.pre_order_bullets?.value),
    },
    sizeGuide,
    subMenu,
    video: returnVideoContentInMedia(product.video),
  }

  return formattedProduct
}

/**
 * Formats price.
 * @param {Object} price - Price response.
 * @returns {Object}
 */
function formatPrice(price) {
  return {
    amount: Number(price?.minVariantPrice.amount) * 100 ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    max: Number(price?.maxVariantPrice.amount) * 100 ?? false,
    min: Number(price?.minVariantPrice.amount) * 100 ?? false,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats images.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatImages(product) {
  return transformEdges(product.images).map((image) => {
    const linkedVariant = product.variants?.length
      ? transformEdges(product.variants).find((variant) => {
        return variant.image.id === image.id
      })
      : false

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(String(image.id)),
      src: image.url,
      variant: decodeApiId(String(linkedVariant?.id)) || false,
      width: image.width,
    }
  })
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} response - GraphQL response.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatVariants(product, isGiftCard) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: Number(variant.compareAtPrice?.amount) * 100 ?? false,
        currencyCode: variant.compareAtPrice?.currencyCode ?? false,
      },
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) ?? false,
      inventory: variant.quantityAvailable,
      physicalStockAvailability: variant.physicalStockAvailability?.value,
      price: {
        amount: Number(variant.price.amount) * 100 ?? false,
        currencyCode: variant.price.currencyCode ?? false,
      },
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(
        formattedVariant.price.amount,
        cnvs.store.moneyFormat,
      )
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/collections/${collection.handle}`
    : `//${location.host}${cnvs.routes.root}/collections/${collection.handle}`

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentPage: 1,
    },
    title: collection.title,
    url,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    return {
      available: product.available,
      compareAtPrice: formatPrice(product.compareAtPriceRange),
      featuredImage: formatImages(product)[0],
      handle: product.handle,
      price: formatPrice(product.priceRange),
      tags: product.tags,
      title: product.title,
      type: product.productType,
      url,
    }
  })

  return formattedCollection
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    return {
      handle,
      links,
      tier,
      title: object.title,
      url: object.url,
    }
  })
}

/**
 * Format Shopify image URL.
 * @param {Object|String} imageUrl - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(imageUrl, options) {
  const src = typeof imageUrl === 'object' ? imageUrl.src : imageUrl

  if (!src.includes('cdn.shopify.com')) {
    return src
  }

  const filetype = src.split('.').reverse()[0]?.split('?')[0]
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    return src
  }

  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove progressive filetype prefix.
   * - Remove filetype.
   * - Remove sizing, crop, and scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace('.progressive.', '.')
    .replace(`.${filetype}`, '.')
    .replace(/(?<size>_(?:\d+)?x(?:\d+)?)?(?<crop>_crop_(?:top|center|bottom|left|right))?(?<scale>@(?:2|3)x)?\./g, '.')

  url = `//${url}${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/**
 * Formats linklist objects for use in ClickableList components.
 * @param {Object} links - Linklist object.
 * @returns {Object}
 */
export function formatLinklist(links) {
  if (!links?.tags) {
    return null
  }

  const badgeTag = links.tags.find(tag => tag.includes('badge='))
  return {
    label: links.title,
    link: links.url,
    showIcon: links.tags.includes('show-icon') || links.hasLinks,
    spaceAbove: links.tags.includes('space-above'),
    badge: badgeTag ? badgeTag.split('badge=')[1] : false,
    image: links.image ? links.image : false,
    video: links.video ? links.video : false,
    links: links.tierTwo ? links.tierTwo : links.links,
    tier: links.tier,
    title_raw: links.title_raw ? links.title_raw : '',
  }
}

/**
 * Formats product from Klevu API call.
 * @param {Object} product - Product object from Klevu response
 * @returns {Object}
 */
export function formatKlevuProduct(product) {
  const handle = formatKlevuProductUrl(product.url)

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${handle}`
    : `//${location.host}${cnvs.routes.root}/products/${handle}`

  let additionalData

  try {
    additionalData = JSON.parse(`{
      "product": ${product.additionalDataToReturn}
    }`)
  } catch {
    additionalData = JSON.parse(`{
      "product": ${product.additionalDataToReturn.replace(/'/g, '"')}
    }`)
  }

  const formattedProduct = {
    available: product.inStock === 'yes',
    compareAtPrice: {
      amount: Number(product.price) * 100,
      currencyCode: product.currency,
    },
    description: product.shortDesc,
    handle,
    id: product.itemGroupId,
    price: {
      amount: Number(product.salePrice) * 100,
      currencyCode: product.currency,
    },
    tags: additionalData.product.tags,
    title: product.name.split(' - ')[0],
    url,
    media: formatKlevuMedia(additionalData.product.media),
    variants: formatKlevuVariant(additionalData.product.variants),
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.images = formattedProduct.media.filter(
    (item) => item.media_type === 'image',
  )

  formattedProduct.featuredImage = formattedProduct.images[0]

  return formattedProduct
}

/**
 * Returns product handle from Klevu product url.
 * @param {String} url
 * @returns {String}
 */
function formatKlevuProductUrl(url) {
  const splitUrl = url.split('products/')
  return splitUrl[splitUrl.length - 1]
}

/**
 * Formats variants.
 * @param {Array} variants - Klevu product variants.
 * @returns {Array}
 */
function formatKlevuVariant(variants) {
  return variants.map((variant) => {
    const formattedVariant = {
      available: variant.available,
      id: decodeApiId(variant.id),
      selectedOptions: variant.selectedOptions,
      title: variant.title.includes(' - ')
        ? variant.title.split(' - ')[1]
        : variant.title,
    }

    return formattedVariant
  })
}

/**
 * Formats media.
 * @param {Array} media - Klevu product media.
 * @returns {Array}
 */
function formatKlevuMedia(media) {
  return media.map((item) => {
    const formattedMedia = {
      alt: item?.alt,
      src: item?.src,
      media_type: item?.mediaType.toLowerCase(),
    }

    return formattedMedia
  })
}

/**
 * Formats product from Depict API call.
 * @param {Object} product - Product object from Depict response
 * @returns {Object}
 */
export function formatDepictProduct(product) {
  const primaryVariant = product.variant_displays[product.variant_index]
  const productListingResultId = product.product_listing_result_id

  const handle = primaryVariant.slug

  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${handle}`
    : `//${location.host}${cnvs.routes.root}/products/${handle}`

  const formattedProduct = {
    available: primaryVariant.in_stock,
    compareAtPrice: {
      amount: Number(primaryVariant.original_price) * 100,
      currencyCode: cnvs.store.currency,
    },
    description: '',
    handle,
    id: primaryVariant.item_group_id,
    variantId: primaryVariant.store_product_id,
    productListingResultId,
    price: {
      amount: Number(primaryVariant.sale_price) * 100,
      currencyCode: cnvs.store.currency,
    },
    tags: primaryVariant.tags,
    title: primaryVariant.title.split(' - ')[0],
    url,
    media: formatDepictMedia(primaryVariant.image_urls, primaryVariant.title),
    variants: formatDepictVariant(product.variant_displays),
    hideOnCollections: formatCollectionHandles(primaryVariant?.product_hide_on_collections),
    flatImageCollections: formatCollectionHandles(primaryVariant?.flat_image_show_on_collections),
    featuredImageIsFlat: primaryVariant?.featured_image_is_flat,
  }

  formattedProduct.featuredImage = formattedProduct.media[0]

  return formattedProduct
}

/**
 * Formats variants.
 * @param {Array} variants - Depict product variants.
 * @returns {Array}
 */
function formatDepictVariant(variants) {
  return variants.map((variant) => {
    const formattedVariant = {
      available: variant.in_stock,
      id: variant.product_id,
      variantId: variant.store_product_id,
      selectedOptions: variant.selected_options,
      title: variant.variant_title || variant.title,
    }

    return formattedVariant
  })
}

/**
 * Formats media.
 * @param {Array} media - Depict product media.
 * @returns {Array}
 */
function formatDepictMedia(media, productTitle) {
  return media.map((item) => {
    const formattedMedia = {
      alt: productTitle,
      src: item,
      media_type: 'image',
    }

    return formattedMedia
  })
}

/**
 * Formats filters from Depict API call.
 * @param {Array} filters - Depict filters.
 * @returns {Array}
 */
export function formatDepictFilters(filters, selectedFilters) {
  return filters.map((filter) => {
    const selectedFilter = selectedFilters.find(sf => sf.key === filter.field)

    const options = filter.meta.values?.map((value, index) => ({
      value,
      name: filter.meta.names[index],
      count: filter.meta.counts[index],
      selected: selectedFilter?.values.includes(value),
    })) || []

    const formattedFilter = {
      id: filter.id,
      key: filter.field,
      name: filter.meta?.group_title,
      op: filter.op,
      options,
    }

    if (filter.meta?.type === 'range') {
      formattedFilter.min = filter.meta.min
      formattedFilter.max = filter.meta.max
    }

    return formattedFilter
  })
}

/**
 * Formats Shopify recommendations response
 * @param {Object} product - Shopify recommendations product.
 * @returns {Object}
 */
export function formatRecommendationsProduct(product) {
  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.root}/products/${product.handle}`

  const compareAtPrice = {
    amount: product.compare_at_price,
    currencyCode: '',
    max: product.compare_at_price_max,
    min: product.compare_at_price_min,
    varies: product.compare_at_price_varies,
  }

  const price = {
    amount: product.price,
    currencyCode: '',
    max: product.price_max,
    min: product.price_min,
    varies: product.price_varies,
  }

  const formattedProduct = {
    available: product.available,
    compareAtPrice,
    description: product.descriptionHtml,
    handle: product.handle,
    id: decodeApiId(String(product.id)),
    images: formatRecommendationsImages(product),
    options: formatOptions(product),
    price,
    tags: product.tags,
    title: product.title,
    type: product.productType,
    url,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.variants = formatRecommendationsVariants(product)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title?.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Format recommendation product images.
 * @param {Array} product - Shopify product.
 * @returns {Array}
 */
function formatRecommendationsImages(product) {
  const images = product.media?.filter(media => media?.media_type === 'image')

  return images?.map((image) => {
    return {
      alt: image.alt,
      height: image.height,
      id: decodeApiId(String(image.id)),
      src: image.src,
      width: image.width,
    }
  })
}

/**
 * Format recommendation product variants.
 * @param {Array} product - Shopify product.
 * @returns {Array}
 */
function formatRecommendationsVariants(product) {
  return product.variants?.map((variant) => {

    const selectedOption = product.options.find(option => {
      return variant.options?.every(varOption => option.values?.includes(varOption))
    })

    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: variant.compare_at_price,
        currencyCode: false,
      },
      id: decodeApiId(String(variant.id)),
      price: {
        amount: variant.price,
        currencyCode: false,
      },
      selectedOptions: variant.options.map((option) => {
        return {
          name: selectedOption.name,
          value: option,
        }
      }),
      title: variant.title,
    }

    return formattedVariant
  })
}

/**
 * Formats collection handles if they are separated by a comma character.
 * @param {Array} collections - Shopify collection handles
 * @returns {Array}
 */
function formatCollectionHandles(collections) {
  if (!collections) {
    return []
  }

  const formattedHandles = collections.reduce((acc, collection) => {
    if (collection.includes(',')) {
      const splitCollections = collection.split(',')

      acc.push(...splitCollections)
    } else {
      acc.push(collection)
    }
    return acc
  }, [])

  return formattedHandles
}
